import { useCallback, type ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import type { DetailSection, DetailStatus } from '@/shared/app-detail-layout/types';
import { useAsyncResource } from '@/shared/hooks/use-async-resource';
import { ApiError } from '@/api/client/problem';
import { CatalogApi, groupOffersByVariant } from '@/modules/catalog/services/api';
import type { CanonicalProduct, CanonicalVariant, Offer } from '@/api/schemas/catalog';

/**
 * One catalogue entry.
 *
 * TWO SECTIONS, TWO AUTHORITIES
 * ------------------------------
 * The product itself is a PUBLIC-scope read, so it is always shown. The tenant's
 * offers are a TENANT-scope read: when the caller holds no `catalog.offer.read`
 * the API refuses, and this page says so in words instead of rendering an empty
 * table — an empty table would say "this tenant sells nothing", which is a
 * different and false statement.
 */

const PUBLICATION_TONE: Record<string, { tone: DetailStatus['tone']; label: string }> = {
  published: { tone: 'positive', label: 'Published' },
  draft: { tone: 'neutral', label: 'Draft' },
  retired: { tone: 'caution', label: 'Retired' },
  merged: { tone: 'neutral', label: 'Merged' },
};

/** The publication state as a status, with a neutral fallback for an unknown value. */
function publicationStatus(state: string | undefined): { tone: DetailStatus['tone']; label: string } {
  return PUBLICATION_TONE[state ?? 'draft'] ?? { tone: 'neutral', label: 'Unknown' };
}

type CatalogDetail = {
  product: CanonicalProduct;
  offers: Offer[];
  offersRestricted: boolean;
};

/**
 * Loads the product and the tenant's offers together.
 *
 * A `403` on the offers is a FACT ABOUT THE ACTOR, not a failure of the page:
 * it is captured as `offersRestricted` so the panel can say so in words. Any
 * other failure propagates and renders as the error state.
 */
async function loadDetail(id: string): Promise<CatalogDetail> {
  const [product, offers] = await Promise.all([
    CatalogApi.getProduct(id),
    CatalogApi.listOffers().then(
      (loaded) => ({ loaded, restricted: false }),
      (cause: unknown) => {
        if (cause instanceof ApiError && cause.status === 403) {
          return { loaded: [] as Offer[], restricted: true };
        }
        throw cause;
      },
    ),
  ]);

  return { product, offers: offers.loaded, offersRestricted: offers.restricted };
}

/** The axis codes a variant carries, e.g. `model DEMO-BASIC · capacity STD`. */
function describeVariant(variant: CanonicalVariant): string {
  const parts: string[] = [];
  if (variant.modelCode) parts.push(`model ${variant.modelCode}`);
  if (variant.capacityCode) parts.push(`capacity ${variant.capacityCode}`);
  if (variant.colorCode) parts.push(`colour ${variant.colorCode}`);
  if (variant.regionCode) parts.push(`region ${variant.regionCode}`);
  if (variant.versionCode) parts.push(`version ${variant.versionCode}`);
  return parts.length > 0 ? parts.join(' · ') : 'No axis values';
}

function formatPrice(offer: Offer): string {
  const { amountMinor, currency } = offer.price;
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amountMinor / 100);
  } catch {
    return `${amountMinor} ${currency}`;
  }
}

/**
 * The header the layout renders for a product.
 *
 * Extracted from the component so the page body reads as structure: what the
 * product is, and what is shown beneath it.
 */
function headerFor(product: CanonicalProduct | null): {
  title: string;
  subtitle: string | undefined;
  status: DetailStatus | undefined;
  badges: ReactNode[] | undefined;
} {
  if (!product) {
    return { title: 'Product', subtitle: undefined, status: undefined, badges: undefined };
  }

  const tone = publicationStatus(product.publicationState);
  return {
    title: product.name,
    subtitle: product.modelCode ?? undefined,
    status: { label: tone.label, tone: tone.tone },
    badges: [<Badge key="kind" tone="neutral">{product.kind}</Badge>],
  };
}

/** True when the load succeeded and there is no product to show. */
function isMissing(product: CanonicalProduct | null, error: Error | null, isLoading: boolean): boolean {
  return !isLoading && error === null && product === null;
}

export function DetailPage({ id }: { id: string }): ReactNode {
  const load = useCallback(() => loadDetail(id), [id]);
  const { data, error, isLoading } = useAsyncResource(load);

  const product = data?.product ?? null;
  const header = headerFor(product);

  const sections: ReadonlyArray<DetailSection> = [
    { id: 'variants', label: 'Variants', content: <VariantTable product={product} /> },
    {
      id: 'offers',
      label: 'Offers',
      content: (
        <OfferPanel
          grouped={groupOffersByVariant(data?.offers ?? [])}
          restricted={data?.offersRestricted ?? false}
          product={product}
        />
      ),
    },
  ];

  return (
    <AppDetailLayout
      title={header.title}
      subtitle={header.subtitle}
      backTo="/catalog/products"
      status={header.status}
      badges={header.badges}
      sections={sections}
      isLoading={isLoading}
      error={error}
      notFound={isMissing(product, error, isLoading)}
    />
  );
}

function VariantTable({ product }: { product: CanonicalProduct | null }): ReactNode {
  if (!product || product.variants.length === 0) {
    return <p className="text-sm text-content-muted">This product has no variants.</p>;
  }

  return (
    <ul className="flex flex-col gap-2">
      {product.variants.map((variant) => (
        <li
          key={variant.id}
          className="flex items-center justify-between gap-4 border-b border-content-muted/20 py-2 last:border-b-0"
        >
          <span className="text-sm text-content">{describeVariant(variant)}</span>
          <span className="font-mono text-xs text-content-muted">{variant.id.slice(0, 8)}</span>
        </li>
      ))}
    </ul>
  );
}

function OfferPanel({
  grouped,
  restricted,
  product,
}: {
  grouped: Map<string, Offer[]>;
  restricted: boolean;
  product: CanonicalProduct | null;
}): ReactNode {
  if (restricted) {
    return (
      <p className="text-sm text-content-muted">
        Offers are not visible for your role. The API refuses the read for this tenant.
      </p>
    );
  }

  const variantIds = product?.variants.map((variant) => variant.id) ?? [];
  const rows = variantIds.flatMap((variantId) => grouped.get(variantId) ?? []);

  if (rows.length === 0) {
    return <p className="text-sm text-content-muted">This tenant sells no offer for these variants yet.</p>;
  }

  return (
    <ul className="flex flex-col gap-2">
      {rows.map((offer) => (
        <li
          key={offer.id}
          className="flex items-center justify-between gap-4 border-b border-content-muted/20 py-2 last:border-b-0"
        >
          <div className="flex flex-col">
            <span className="text-sm text-content">{offer.title ?? offer.sku}</span>
            <span className="text-xs text-content-muted">
              {offer.sku} · {offer.state} · {offer.kind}
            </span>
          </div>
          <span className="text-sm font-medium text-content">{formatPrice(offer)}</span>
        </li>
      ))}
    </ul>
  );
}
